import { useEffect, useMemo, useRef } from 'react';
import {
  clearExtraDetails,
  dispatch,
  ErrorVariants,
  FileUploadOutput,
  getWorkspaceId,
  showToast,
  useAppSelector,
  useCreateInvoiceMutation,
  useListOrdersMutation,
  useUpdateInvoiceMutation,
  useUploadWorkspaceDocumentsMutation,
} from '@zonofi/zono-money-store';
import { useNavigate, useParams } from 'react-router-dom';
import { OrderStatusEnum, SortDirection } from '../../constants';
import moment from 'moment';
import { UploadToS3 } from '@zonofi/common/functions/s3-upload';
import { invoice_details } from '../upload-invoice';
import { dispatch_Vehicle_Details } from '../../components';
import { validateFields } from '../../helper';

interface UploadInvoiceProps {
  uploadedMedia: FileUploadOutput[];
  formData: Record<string, string | string[]>;
  channelId?: string;
  setFormData: React.Dispatch<
    React.SetStateAction<Record<string, string | string[]>>
  >;
  setOpenSections: React.Dispatch<
    React.SetStateAction<Record<string, boolean>>
  >;
  isUpdateCustomer?: boolean;
  openSections?: Record<string, boolean>;
}

export const useUploadInvoice = ({
  uploadedMedia,
  formData,
  channelId,
  setOpenSections,
  setFormData,
  isUpdateCustomer,
  openSections,
}: UploadInvoiceProps) => {
  const { customerId = '' } = useParams();
  const navigate = useNavigate();

  const sellerWorkspaceId = useAppSelector(getWorkspaceId);
  const [fetchOrdersApi, ordersApiResponse] = useListOrdersMutation();
  const [createInvoice, createInvoiceResponse] = useCreateInvoiceMutation();
  const [updateInvoice, updateInvoiceResponse] = useUpdateInvoiceMutation();

  const [uploadDocument, uploadDocumentResponse] =
    useUploadWorkspaceDocumentsMutation();

  useEffect(() => {
    fetchOrdersApi({
      params: {
        workspaceId: sellerWorkspaceId,
        customerId: customerId,
      },
      body: {
        workspaceId: sellerWorkspaceId,
        customerId: customerId,
        skip: 1,
        sortBy: 'orderPlacedAt',
        sortDirection: SortDirection.DESCENDING,
        includeInvoice: true,
        startDate: moment()
          .subtract(1, 'year')
          .startOf('year')
          .format()
          .toString(),
        endDate: moment().endOf('day').format().toString(),
        pageNo: 1,
        pageSize: 50,
        filterModel: {
          headDivisionIds: [],
          divisionIds: [],
          cfaIds: [],
          status: [
            OrderStatusEnum.APPROVED,
            OrderStatusEnum.CONFIRMED,
            OrderStatusEnum.WAITING_FOR_CONFIRMATION,
          ],
          customerIds: [customerId],
        },
      },
    });
  }, []);

  const orderDropdownOptions = useMemo(() => {
    if (!ordersApiResponse?.data?.order) return [];

    return ordersApiResponse.data.order
      .filter((order) => order?.orderMetaData?.refOrderNumber)
      .map((order) => ({
        label: order.orderMetaData.refOrderNumber,
        value: String(order.id),
      }));
  }, [ordersApiResponse]);

  const handleChange = (key: string, value: string) => {
    setFormData((prev) => {
      if (key === 'totalBox') {
        return {
          ...prev,
          totalBox: value,
          totalWeight: value ? '' : prev.totalWeight,
        };
      }

      if (key === 'totalWeight') {
        return {
          ...prev,
          totalWeight: value,
          totalBox: value ? '' : prev.totalBox,
        };
      }

      return {
        ...prev,
        [key]: value,
      };
    });
  };

  const handleToggle = (key: string) => {
    setOpenSections((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const isFormValid = useMemo(() => {
    const areInvoiceFieldsValid = validateFields(formData, invoice_details);
    const shouldValidateDispatch = openSections?.dispatch === true;
    const areDispatchFieldsValid = shouldValidateDispatch
      ? validateFields(formData, dispatch_Vehicle_Details)
      : true;

    return (
      areInvoiceFieldsValid &&
      areDispatchFieldsValid &&
      uploadedMedia.length > 0
    );
  }, [formData, uploadedMedia, openSections]);

  const runOnError = (error: any) => {
    let errorMessage = 'Failed to create invoice';

    if ('data' in error) {
      const fetchError = error as any;
      errorMessage =
        (fetchError.data as { message?: string })?.message || errorMessage;
    } else if ('message' in error) {
      errorMessage = error.message || errorMessage;
    }

    setTimeout(() => {
      dispatch(
        showToast({
          message: errorMessage,
          type: ErrorVariants.DANGER,
          canDisplay: true,
          responseStatus: 'Error',
        })
      );
    }, 100);
  };

  const onUploadInvoice = (docUri?: string, invoiceId?: string) => {
    const payload = {
      workspaceId: sellerWorkspaceId,
      invoiceAmount: Number(String(formData?.invoiceAmount)?.replace(/,/g, '')),
      invoiceDate: formData?.invoiceDate
        ? moment(formData.invoiceDate, 'DD/MM/YYYY').format('YYYY-MM-DD')
        : '',
      dueDate: formData?.dueDate
        ? moment(formData.dueDate, 'DD/MM/YYYY').format('YYYY-MM-DD')
        : '',
      docNumber: formData?.invoiceNumber,
      inviteId: customerId,
      description: formData?.note,
      orderIds: Array.isArray(formData?.orderNumbers)
        ? formData.orderNumbers.map(Number)
        : [],
      ...(docUri ? { docs: docUri } : { dontPublishEvent: true }),
      docType: 'INV',
      channelId,
      ...(formData?.dispatchDocNo && formData?.dispatchDate
        ? {
            dispatchDetails: {
              dispatchDocNumber: formData?.dispatchDocNo ?? '',
              ...(formData?.dispatchDate
                ? {
                    dispatchDate: moment(formData.dispatchDate).format(
                      'YYYY-MM-DD'
                    ),
                  }
                : {}),
              type: 'invoice',
              numOfBoxes: formData?.totalBox ?? '',
              totalWeightINKg: formData?.totalWeight ?? '',
              transporterName: formData?.driverName ?? '',
              vehicleNumber: formData?.vehicleNumber ?? '',
              driverName: formData?.driverName ?? '',
              driverPhone: formData?.driverPhone ?? '',
              destinationCity: formData?.cityName ?? '',
              destinationZipCode: formData?.Pincode ?? '',
              destinationAddress: formData?.address ?? '',
              termsOfDelivery: formData?.termsOfDelivery ?? '',
            },
          }
        : {}),
    };
    if (docUri) {
      updateInvoiceResponse.reset();
      updateInvoice({ ...payload, invoiceId });
    } else {
      createInvoiceResponse.reset();
      createInvoice(payload);
    }
  };

  useEffect(() => {
    if (createInvoiceResponse.isSuccess && createInvoiceResponse.data) {
      // FIRST SUCCESS → upload files
      if (uploadedMedia?.length > 0) {
        uploadFilesToS3(createInvoiceResponse?.data?.id);
        return;
      }
    } else if (createInvoiceResponse.isError) {
      const error = createInvoiceResponse.isError;
      if (error) {
        runOnError(error);
      }
    }
  }, [createInvoiceResponse]);

  useEffect(() => {
    if (updateInvoiceResponse.isSuccess) {
      setTimeout(() => {
        dispatch(
          showToast({
            message: 'Invoice created successfully',
            type: ErrorVariants.SUCCESS,
            canDisplay: true,
            responseStatus: 'Success',
          })
        );
      }, 200);
      dispatch(clearExtraDetails());
      navigate(-1);
    } else if (updateInvoiceResponse.isError) {
      const error = updateInvoiceResponse.error;
      if (error) {
        runOnError(error);
      }
    }
  }, [updateInvoiceResponse]);

  const uploadFilesToS3 = async (invoiceId: string) => {
    let presignedUrls: string[] = [];
    try {
      const uploadPromises = uploadedMedia.map(async (item) => {
        const response = await uploadDocument({
          workspaceId: sellerWorkspaceId,
          documentType: 'invoice',
          entityId: invoiceId,
          mimeType: item?.file?.type,
          subType: 'official',
        }).unwrap();

        if (response?.presignedUrl) {
          const presignedUrl = response.presignedUrl;
          presignedUrls.push(presignedUrl);

          await UploadToS3(presignedUrl, {
            uri: item.uri!.toString(),
            type: item.file?.type,
          });
        }
      });

      await Promise.all(uploadPromises);
    } catch (error) {
      dispatch(
        showToast({
          canDisplay: true,
          message: 'Failed to upload file(s)',
          responseStatus: 'Warning',
          type: ErrorVariants.WARNING,
        })
      );
    }

    if (presignedUrls.length > 0) {
      onUploadInvoice(presignedUrls[0], invoiceId);
    }
  };

  return {
    orderDropdownOptions,
    isFormValid,
    onUploadInvoice,
    handleChange,
    handleToggle,
    isLoading:
      createInvoiceResponse?.isLoading ||
      uploadDocumentResponse.isLoading ||
      updateInvoiceResponse.isLoading,
  };
};
